import React, { useState, useEffect } from 'react';
import VideoDisplayArea from '../video/VideoDisplayArea';
import InterviewSidebar from '../sidebar/InterviewSidebar';

interface InterviewMainContentProps {
  isMobileView: boolean;
}

/**
 * InterviewMainContent - Main content area with video and sidebar
 * 
 * This component is responsible for:
 * 1. Organizing the grid layout for the main content
 * 2. Managing responsive behavior between desktop and mobile
 * 3. Handling the video display and sidebar components
 */
const InterviewMainContent: React.FC<InterviewMainContentProps> = ({ isMobileView }) => {
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Add CSS for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .interview-layout-transition {
        transition: all 300ms cubic-bezier(0.4, 0.0, 0.2, 1);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Listen for transcript toggle events
  useEffect(() => {
    const handleTranscriptToggle = (event: CustomEvent) => {
      setIsAnimating(true);
      setTranscriptExpanded(event.detail.expanded);
      
      // Reset animation flag after transition completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    };
    
    window.addEventListener('transcript-toggle', handleTranscriptToggle as EventListener);
    
    return () => {
      window.removeEventListener('transcript-toggle', handleTranscriptToggle as EventListener);
    };
  }, []);
  
  return (
    <main 
      className="interview-main-content mx-auto w-full max-w-7xl px-4 md:px-4 flex flex-col"
      style={{ 
        paddingLeft: '0.8rem',
        paddingRight: '0.8rem',
        marginTop: '5.8rem', /* Space for navbar */
        marginBottom: isMobileView ? '9.3rem' : '9.4rem', /* Space for control bar */
        height: `calc(100vh - ${isMobileView ? '11.6rem' : '12.2rem'})`, /* Viewport height minus (navbar + control bar) */
        minHeight: '0',
        overflow: 'hidden'
      }}
    >
      <div 
        className={`grid ${isMobileView 
          // ? transcriptExpanded 
          //   ? 'grid-rows-[48%_50%]' 
          //   : 'grid-rows-[84%_14%]'
          ? 'grid-rows-[100%]'
          : 'grid-cols-1 lg:grid-cols-3'} gap-3 h-full flex-grow overflow-hidden interview-layout-transition`}
      >
        {/* Left & Center content - AI Interviewer and User Video */}
        <div className={`${isMobileView ? 'h-full' : 'lg:col-span-2 lg:order-1 order-2 h-full flex flex-col lg:mb-0 mb-3'} min-h-0 interview-layout-transition`}>
          <div className="grid grid-cols-1 gap-3 h-full min-h-0 flex-shrink-1 flex-grow-1">
            {/* Main display area - AI interviewer or user video */}
            <VideoDisplayArea isMobileView={isMobileView} />
          </div>
        </div>
        
        {/* Right side - Sidebar with transcript and details */}
        {!isMobileView && (
          <InterviewSidebar isMobileView={isMobileView} />
        )}
      </div>
    </main>
  );
};

export default InterviewMainContent; 